import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ShieldCheck, CheckCircle2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AcceptInvite() {
  const { token } = useParams();
  const nav = useNavigate();
  const { setUser } = useAuth();

  const [invite, setInvite] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/invites/${token}`);
        setInvite(data);
        setName(data.name || "");
      } catch (e) {
        setLoadError(e.response?.data?.detail || "Invitation link is invalid or expired.");
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  const submit = async () => {
    if (password.length < 8) return toast.error("Password must be at least 8 characters");
    if (password !== confirm) return toast.error("Passwords do not match");
    setSubmitting(true);
    try {
      const { data } = await axios.post(
        `${API}/invites/${token}/accept`,
        { password, name: name || invite.name || undefined },
        { withCredentials: true },
      );
      // The backend sets the `vqms_access_token` HttpOnly cookie on the
      // response — no need to persist the token in JS-accessible storage.
      setUser(data.user);
      toast.success(`Welcome to VitaQMS, ${data.user.name || data.user.email}`);
      nav("/dashboard");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not accept invitation");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 text-white flex items-center justify-center p-6">
      <div className="w-full max-w-md" data-testid="accept-invite-card">
        <Link to="/" className="flex items-center gap-2 mb-8 justify-center">
          <div className="w-9 h-9 bg-white rounded flex items-center justify-center">
            <ShieldCheck className="w-4 h-4 text-slate-900" />
          </div>
          <div>
            <div className="font-display font-extrabold text-white text-xl leading-none">VitaQMS</div>
            <div className="text-[10px] font-mono text-slate-400 mt-0.5">by PerCen360</div>
          </div>
        </Link>

        {loading && (
          <div className="text-center text-slate-400 py-10" data-testid="accept-invite-loading">
            Verifying your invitation…
          </div>
        )}

        {loadError && (
          <div className="rounded-lg border border-rose-400/30 bg-rose-500/10 p-6 text-center" data-testid="accept-invite-error">
            <AlertTriangle className="w-8 h-8 text-rose-300 mx-auto mb-3" />
            <div className="font-display font-bold text-lg text-white mb-1">Can&apos;t use this link</div>
            <p className="text-sm text-slate-300">{loadError}</p>
            <p className="text-xs text-slate-400 mt-4">Ask your VitaQMS admin to send you a fresh invitation.</p>
            <Link to="/login" className="inline-block mt-6 text-sm text-slate-300 underline hover:text-white">Go to login</Link>
          </div>
        )}

        {invite && !loadError && (
          <div className="rounded-lg border border-white/10 bg-white/5 p-6 backdrop-blur-md">
            <div className="text-center mb-6">
              <div className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-wider text-emerald-300 bg-emerald-500/10 border border-emerald-400/20 px-2 py-1 rounded">
                <CheckCircle2 className="w-3 h-3" /> Invitation valid
              </div>
              <h1 className="font-display font-extrabold text-2xl text-white mt-4">Welcome to VitaQMS</h1>
              <p className="text-sm text-slate-400 mt-2">
                <span className="font-mono">{invite.invited_by_email}</span> invited you as <span className="font-mono text-slate-200">{invite.email}</span>.
              </p>
              {(invite.roles || []).length > 0 && (
                <div className="mt-3 flex flex-wrap gap-1 justify-center">
                  {invite.roles.map((r) => (
                    <span key={r} className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-emerald-500/20 text-emerald-200 border border-emerald-400/30">
                      {r}
                    </span>
                  ))}
                </div>
              )}
            </div>

            <div className="space-y-3">
              <div>
                <Label className="text-xs font-semibold text-slate-300">Your name</Label>
                <Input data-testid="accept-invite-name" value={name} onChange={(e) => setName(e.target.value)}
                       className="mt-1.5 bg-slate-900 border-white/10 text-white" />
              </div>
              <div>
                <Label className="text-xs font-semibold text-slate-300">Create a password</Label>
                <Input data-testid="accept-invite-password" type="password" value={password}
                       onChange={(e) => setPassword(e.target.value)}
                       className="mt-1.5 bg-slate-900 border-white/10 text-white"
                       placeholder="At least 8 characters" />
              </div>
              <div>
                <Label className="text-xs font-semibold text-slate-300">Confirm password</Label>
                <Input data-testid="accept-invite-confirm" type="password" value={confirm}
                       onChange={(e) => setConfirm(e.target.value)}
                       className="mt-1.5 bg-slate-900 border-white/10 text-white" />
              </div>
              <Button onClick={submit} disabled={submitting}
                      data-testid="accept-invite-submit"
                      className="w-full h-11 bg-white text-slate-900 hover:bg-[#0055FF] hover:text-white font-semibold">
                {submitting ? "Setting up your account…" : "Create account & log in"}
              </Button>
              <p className="text-[10px] font-mono text-slate-500 text-center pt-2">
                Invite expires {new Date(invite.expires_at).toLocaleString()}
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
